import React, { useEffect } from "react";
import { motion } from "framer-motion";
import PageHeader from "./PageHeader";

const PromptGeneratorPage = () => {
  useEffect(() => {
    document.title = "ChatGPT Power-Up: Prompt Snippets - Save & Reuse Prompts";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Save and reuse your favorite prompts with ChatGPT Power-Up. Create prompt snippets for faster, more efficient conversations.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-4xl mb-4">⚡</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Prompt Snippets
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Save your most-used prompts and access them instantly. Never type the same prompt twice with our powerful snippet system.
          </p>
        </motion.div>

        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">💾</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Save Your Prompts</h3>
            <p className="text-gray-600">
              Create and save your most frequently used prompts as snippets. Organize them by category for easy access.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">⚡</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Instant Access</h3>
            <p className="text-gray-600">
              Access your saved prompts with a single click. No more copying and pasting - just click and use.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">🔄</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Smart Variables</h3>
            <p className="text-gray-600">
              Use variables in your snippets to make them dynamic. Insert context-specific information on the fly.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <div className="text-3xl mb-4">📱</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Seamless Integration</h3>
            <p className="text-gray-600">
              Works perfectly within ChatGPT's native interface. No external tools or complicated setups required.
            </p>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center bg-white p-8 rounded-xl shadow-md"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Supercharge Your ChatGPT Workflow?
          </h2>
          <p className="text-gray-600 mb-6">
            Join thousands of users who have transformed their ChatGPT experience with prompt snippets.
          </p>
          <a
            href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
          >
            <img src="/chrome.png" alt="Chrome" className="w-5 h-5" />
            Install Now - Free
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default PromptGeneratorPage; 